import { useCallback, useRef, useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { WikiArticle } from '@/lib/wikipedia';

interface WikiArticleViewProps {
  article: WikiArticle | null;
  isLoading: boolean;
  error?: string | null;
  onLinkClick: (title: string) => void;
}

export function WikiArticleView({ article, isLoading, error, onLinkClick }: WikiArticleViewProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayedArticle, setDisplayedArticle] = useState<WikiArticle | null>(null);

  // Click handler for Wikipedia links
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');
    
    if (link) {
      e.preventDefault();
      e.stopPropagation();
      
      const title = link.getAttribute('data-title');
      if (title) {
        onLinkClick(title);
      }
    }
  }, [onLinkClick]);

  // Smooth transition between articles
  useEffect(() => {
    if (article && article.title !== displayedArticle?.title) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setDisplayedArticle(article);
        setIsTransitioning(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [article, displayedArticle?.title]);

  // Scroll to top when article changes
  useEffect(() => {
    if (displayedArticle && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [displayedArticle?.title]);

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4 text-center px-4">
          <AlertCircle className="w-8 h-8 text-destructive" />
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!displayedArticle) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No article loaded</p>
      </div>
    );
  }

  return (
    <div className={`h-full overflow-hidden flex flex-col transition-opacity duration-150 ${
      isTransitioning ? 'opacity-50' : 'opacity-100'
    }`}>
      {/* Article title */}
      <div className="px-6 py-4 border-b border-border bg-wiki-content-bg/50">
        <h1 className="text-2xl md:text-3xl font-bold">{displayedArticle.title}</h1>
      </div>

      {/* Article content - onClick handler directly on div */}
      <div
        ref={contentRef}
        onClick={handleClick}
        className="flex-1 overflow-y-auto scrollbar-thin px-6 py-4 bg-wiki-content-bg/30"
      >
        <div
          className="wiki-content max-w-4xl mx-auto"
          dangerouslySetInnerHTML={{ __html: displayedArticle.html }}
        />
      </div>
    </div>
  );
}
